/* BPC cart repair: keep confirmed cart additions separate from display errors. */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      if (this.variantIdInput) this.variantIdInput.disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = this.getUsableCartElement();
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton?.querySelector('span');
      if (this.submitButton && document.querySelector('cart-drawer')) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }
      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    getUsableCartElement() {
      return [document.querySelector('cart-drawer'), document.querySelector('cart-notification')]
        .find(cart => cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function') || null;
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.error = false;
      this.handleErrorMessage();
      this.querySelector('[data-bpc-cart-status]')?.remove();
      const quantityInput = Array.from(this.form.elements).find(field => field.name === 'quantity' && !field.disabled);
      if (quantityInput && window.BpcQuantityRules) {
        const error = window.BpcQuantityRules.validate(quantityInput);
        if (error) {
          this.handleErrorMessage(error);
          quantityInput.setAttribute('aria-invalid', 'true');
          quantityInput.focus();
          return;
        }
      }
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      const spinner = this.submitButton.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden');
      let addConfirmed = false;
      try {
        const formData = new FormData(this.form);
        if (quantityInput) formData.set('quantity', quantityInput.value);
        this.cart = this.getUsableCartElement();
        if (this.cart) {
          try {
            const sections = this.cart.getSectionsToRender().map(section => section.id).filter(Boolean);
            if (sections.length) {
              formData.set('sections', sections.join(','));
              formData.set('sections_url', window.location.pathname);
            }
            this.cart.setActiveElement?.(document.activeElement);
          } catch (error) {
            console.error('BPC cart section preparation failed; adding without drawer sections.', error);
            formData.delete('sections');
            formData.delete('sections_url');
            this.cart = null;
          }
        }
        const root = window.Shopify?.routes?.root || '/';
        const addUrl = typeof routes !== 'undefined' && routes.cart_add_url ? routes.cart_add_url : `${root}cart/add.js`;
        const httpResponse = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const response = await httpResponse.json();
        if (!httpResponse.ok || response.status) {
          const message = this.getCartError(response);
          this.handleErrorMessage(message);
          await this.publishSafely('cartError', {
            source: 'product-form', productVariantId: formData.get('id'),
            errors: response.errors || response.description, message,
          });
          return;
        }
        // From here onwards, never report a display/subscriber error as a failed add.
        addConfirmed = true;
        const cart = this.cart;
        let displayUpdated = false;
        if (cart) {
          const quickAddModal = this.closest('quick-add-modal');
          try {
            // hide() clears the modal and releases its focus trap synchronously.
            // Await rendering before publishing, so new drawer items are not
            // immediately replaced by a second, competing cart refresh.
            if (quickAddModal) quickAddModal.hide(true);
            const rendered = await cart.renderContents(response);
            displayUpdated = rendered !== false;
          } catch (error) {
            console.error('BPC cart drawer could not refresh after a successful add.', error);
          }
        }
        if (!displayUpdated) this.showCartStatus('Item added to your basket.');
        if (!displayUpdated) this.showAddedState();
        await this.publishSafely('cartUpdate', {
          source: 'product-form', productVariantId: formData.get('id'), cartData: response, cartRendered: displayUpdated,
        });
        try { triggerVatChange(); } catch (error) { console.error('BPC VAT display could not refresh.', error); }
      } catch (error) {
        console.error('BPC cart request:', error);
        if (addConfirmed) {
          this.showAddedState();
          this.showCartStatus('Item added to your basket.');
        } else {
          // A lost response does not prove Shopify rejected the request. Do not retry automatically.
          this.handleErrorMessage('We could not confirm whether this item was added. Please check your basket before trying again.');
          this.showCartStatus('Check your basket before adding this item again.');
        }
      } finally {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    }

    getCartError(response) {
      if (typeof response.description === 'string') return response.description;
      if (typeof response.errors === 'string') return response.errors;
      if (response.errors && typeof response.errors === 'object') {
        return Object.entries(response.errors).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' ');
      }
      return response.message || 'Shopify could not add this item. Please check the quantity and try again.';
    }

    async publishSafely(eventName, data) {
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[eventName]) {
          await publish(PUB_SUB_EVENTS[eventName], data);
        }
      } catch (error) {
        console.error(`BPC ${eventName} subscriber could not refresh.`, error);
      }
    }

    showCartStatus(message) {
      let status = this.querySelector('[data-bpc-cart-status]');
      if (!status) {
        status = document.createElement('p');
        status.setAttribute('data-bpc-cart-status', '');
        status.setAttribute('role', 'status');
        this.appendChild(status);
      }
      const link = document.createElement('a');
      link.href = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : `${window.Shopify?.routes?.root || '/'}cart`;
      link.textContent = 'View basket';
      link.className = 'link underlined-link';
      status.replaceChildren(document.createTextNode(`${message} `), link);
    }

    showAddedState() {
      if (!this.submitButtonText) return;
      const originalText = window.variantStrings?.addToCart || 'Add to basket';
      this.submitButtonText.textContent = 'Added';
      clearTimeout(this.addedStateTimer);
      this.addedStateTimer = setTimeout(() => {
        if (!this.submitButton.disabled) this.submitButtonText.textContent = originalText;
      }, 2000);
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      let wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!wrapper && errorMessage) {
        wrapper = document.createElement('div');
        wrapper.className = 'product-form__error-message-wrapper';
        wrapper.setAttribute('role', 'alert');
        const errorText = document.createElement('span');
        errorText.className = 'product-form__error-message';
        wrapper.appendChild(errorText);
        this.prepend(wrapper);
      }
      const text = wrapper?.querySelector('.product-form__error-message');
      if (!wrapper || !text) return;
      wrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) text.textContent = errorMessage;
    }

    toggleSubmitButton(disable = true, text) {
      if (!this.submitButton) return;
      clearTimeout(this.addedStateTimer);
      if (disable) {
        this.submitButton.setAttribute('disabled', 'disabled');
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      } else {
        this.submitButton.removeAttribute('disabled');
        if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to basket';
      }
    }
    get variantIdInput() { return this.form.querySelector('[name="id"]'); }
  });
}

function triggerVatChange() {
  const vatSwitcher = document.querySelector('#vat-switcher');
  if (!vatSwitcher) return;
  if (window.jQuery) window.jQuery(vatSwitcher).trigger('change');
  else vatSwitcher.dispatchEvent(new Event('change', { bubbles: true }));
}
